// **The engine, across the `wasm-bindgen` boundary** (`rounds-practice-sandbox` task 5.12; design
// **D-A**, **D-C**; DESIGN §2 "a practice run's simulation … executes on the player's device").
//
// This file is where the claim stops being architectural. `waggle-wasm` binds the run (task 5.10) and
// the compile (5.11); `practice-worker.ts` owns the driver; this is the adapter that makes the first
// satisfy the second's {@link PracticeEngine} seam, plus the {@link PracticeDocumentCompiler} over the
// same module. Until it existed, `PracticeEngine`'s only implementation in the repo was a test double.
//
// ## Why the module is a structural interface and never an import
//
// Nothing here imports `client/src/generated/wasm/`. That directory is build output
// (`cargo xtask build-wasm-client`, gitignored), so a static — or even a statically *resolvable*
// dynamic — import of it would break `tsc` and `vite build` on a fresh clone that has not run the
// provisioning step. `practice-trace-store.ts`'s `WasmChecksumModule` and
// `practice-runner.ts`'s `PracticeDocumentCompiler` both already establish this pattern and state the
// reason; {@link PracticeWasmModule} follows it, one degree wider.
//
// It buys a second thing worth naming: the interface below is **hand-written and reviewable**, so the
// exact surface the client depends on is legible here rather than inferred from a generated `.d.ts`
// that no diff shows. `practice-engine.test.ts` checks it against the real module.
//
// ## Two lifetimes, and one of them matters ~27 000 times a run
//
// Every `#[wasm_bindgen]` struct that crosses the boundary is a JavaScript wrapper around a pointer
// into wasm memory, freed by `free()` or eventually by a `FinalizationRegistry`. For the outcome and
// the configure result that distinction is academic — one of each per run. For
// {@link PracticeWasmStepBytes} it is not: one per stepped tick, ~27 000 a season, each holding three
// `Vec<u8>` allocations. Leaning on the registry there means the wasm heap grows for the length of a
// run and is reclaimed only when the collector decides to look, on the device with the least memory
// to spare. So every step wrapper is freed the moment its bytes have been read — **including on the
// throw path**, which is what the `finally` in {@link drainStep} is for. The getters are
// `getter_with_clone`, so the arrays handed back are copies and freeing afterwards is safe.

import type { RunDiagnostic } from '../../ui/colony/run-controller';
import { typeAtLine } from '../../ui/colony/document-slice';
// `PracticeDocumentCompiler` is imported for the `{@link}` references below — the compile factories
// return the narrower synchronous shape, which is assignable to it.
import type {
  PracticeCompileOutcome,
  PracticeDocumentCompiler,
  PracticeRunPlan,
  PracticeRunSummary,
} from './practice-runner';
import type {
  PracticeEngine,
  PracticeEngineOutcome,
  PracticeEngineRun,
  PracticeStatsReducer,
  PracticeStatsSpan,
  PracticeStepOutput,
} from './practice-worker';

// ---------------------------------------------------------------------------------------------
// The module surface, declared structurally
// ---------------------------------------------------------------------------------------------

/** Anything the module hands back that owns wasm memory. `free` is optional in the type so a plain
 * object double satisfies the interface without pretending to own a heap. */
interface WasmOwned {
  free?: () => void;
}

/** One B++ diagnostic, in the **document's** line/column space (`waggle_wasm::practice`'s
 * `PracticeDiagnostic`). */
export interface PracticeWasmDiagnostic extends WasmOwned {
  readonly message: string;
  readonly line: number;
  readonly column: number;
}

/** One tick's framed trace bytes (`PracticeStepBytes`). Every getter clones. */
export interface PracticeWasmStepBytes extends WasmOwned {
  readonly detail: Uint8Array;
  readonly index: Uint8Array;
  /** Absent on the ticks that carry no coarse sample. */
  readonly coarse: Uint8Array | undefined;
  readonly tick: number;
  readonly terminal: boolean;
}

/** One colony's score row (`PracticeColonyScore`). */
export interface PracticeWasmColonyScore extends WasmOwned {
  readonly colony: number;
  /** Living bees including the queen, excluding brood — the **G1** score. */
  readonly living_count: number;
  readonly pool: number;
}

/** A completed run's readout (`PracticeRunOutcome`). */
export interface PracticeWasmRunOutcome extends WasmOwned {
  readonly manifest: Uint8Array;
  readonly balance: Uint8Array;
  /** The terminal coarse flush; empty when the terminal tick was already cadence-aligned. */
  readonly coarse: Uint8Array;
  readonly terminal_tick: number;
  readonly terminal_condition: string;
  readonly sim_outcome: string;
  /** A decimal `u64` string — it exceeds 2^53. */
  readonly state_hash: string;
  readonly score: number;
  readonly first_contact_tick: number | undefined;
  readonly colony_count: number;
  readonly colonies: readonly PracticeWasmColonyScore[];
  /** The run's type roster, as JSON (`waggle_wasm::stats::roster_json`) — carried through unread by
   * this file so {@link createPracticeEngine}'s `openReducer` can hand it to a reduction without
   * inventing it. See {@link PracticeEngineOutcome.roster}. */
  readonly roster: string;
}

/** One bounded byte range of `detail.bin` (`waggle_wasm::stats::DetailSpan`). Every getter clones —
 * see the module header's "Two lifetimes" for why that is what makes freeing it safe. */
export interface PracticeWasmDetailSpan extends WasmOwned {
  readonly offset: number;
  readonly length: number;
}

/**
 * **A local statistics reduction in progress, across the boundary**
 * (`waggle_wasm::stats::LocalStatsReducer`). `nextSpan`/`feed`/`finish` are exactly the three calls
 * {@link PracticeStatsReducer} declares — this is the wasm object those three are read off of.
 */
export interface PracticeWasmStatsReducer extends WasmOwned {
  nextSpan(): PracticeWasmDetailSpan | undefined;
  /** @throws the reducer's own refusal, as a thrown JavaScript string. */
  feed(bytes: Uint8Array): void;
  /** @throws the reducer's own refusal, as a thrown JavaScript string. */
  finish(): string;
}

/**
 * The reducer's constructor, as the module actually exposes it: a **static method on the exported
 * `LocalStatsReducer` class**, not a free function — `#[wasm_bindgen] impl LocalStatsReducer`'s own
 * shape, carried through unchanged rather than flattened onto {@link PracticeWasmModule}.
 */
export interface PracticeWasmStatsReducerCtor {
  openLocalStatsReducer(
    manifestJson: Uint8Array,
    coarse: Uint8Array,
    index: Uint8Array,
    balanceToml: Uint8Array,
    rosterJson: string,
  ): PracticeWasmStatsReducer;
}

/** One configured run, stepped a tick at a time (`PracticeRunHandle`). */
export interface PracticeWasmRunHandle extends WasmOwned {
  readonly terminal_tick: number;
  /** The field's edge length under the balance this run resolved — the quantization denominator, for
   * a watched extent that has no manifest to read it off yet. */
  readonly field_size: number;
  readonly ticks_observed: number;
  readonly failure: string | undefined;
  readonly failure_detail: string | undefined;
  readonly cancelled: boolean;
  step(): PracticeWasmStepBytes | undefined;
  finish(): PracticeWasmRunOutcome | undefined;
  cancel(): void;
}

/** What `practice_configure` answers. **`refusal` being absent is the success arm** — the two arms
 * are one struct because `wasm-bindgen` cannot return a Rust enum carrying an exported type. */
export interface PracticeWasmConfigureResult extends WasmOwned {
  readonly refusal: string | undefined;
  readonly diagnostics: readonly PracticeWasmDiagnostic[];
  /** Yields the run **once**; a second call answers nothing rather than a second handle onto the
   * same `Sim`. */
  take_run(): PracticeWasmRunHandle | undefined;
}

/** What `compile_practice_document` answers. */
export interface PracticeWasmCompileResult extends WasmOwned {
  readonly compiles: boolean;
  readonly diagnostics: readonly PracticeWasmDiagnostic[];
}

/**
 * The `waggle-wasm` surface the practice path uses, and nothing else.
 *
 * Deliberately narrow: the module exports more (the trace file-name constants, the preset document),
 * and a wider declaration here would invite this file to grow a dependency on something it does not
 * need.
 */
export interface PracticeWasmModule {
  /** The rival count a ranked practice run simulates against, read from the crate that owns it. */
  practice_rivals(): number;
  practice_configure(
    runId: string,
    seed: string,
    rivals: number,
    document: string,
    balancePreset: string,
    balanceToml?: string | null,
  ): PracticeWasmConfigureResult;
  compile_practice_document(
    document: string,
    balanceToml?: string | null,
  ): PracticeWasmCompileResult;
  verify_component_checksum(expectedHex: string, bytes: Uint8Array): boolean;
  /**
   * The local statistics reducer's exported class (`trace-colony-statistics`).
   *
   * Optional on this declaration — not because the shipped module ever omits it, but because
   * `practice-engine.test.ts`'s module double predates this capability and a required field would
   * force every one of that file's `PracticeWasmModule` literals to grow one, for a translation this
   * change does not touch. {@link createPracticeEngine} treats its absence exactly as
   * `PracticeEngine.openReducer`'s own doc comment says a caller must: no reduction, not a crash.
   */
  LocalStatsReducer?: PracticeWasmStatsReducerCtor;
}

/**
 * The name the engine's **embedded** preset is truthfully recorded under, when a run is handed no
 * recorded balance at all.
 *
 * `wasm32` has no filesystem, so `Balance::seed()` — `include_str!`-embedded — is the only preset a
 * device can reach without being handed TOML. `practice_configure` takes the provenance name as a
 * plain string, so *something* has to be passed, and naming anything else over the embedded values
 * would produce a manifest whose provenance contradicts its own balance component.
 *
 * This is a constant on the client mirroring `waggle_server::config::BALANCE_PRESET_SEED`, so
 * `practice-engine.test.ts` asserts it against a manifest the **engine** wrote rather than trusting
 * the mirror: a drift would otherwise be invisible until a trace's provenance was read.
 */
export const EMBEDDED_BALANCE_PRESET = 'seed';

// ---------------------------------------------------------------------------------------------
// The engine
// ---------------------------------------------------------------------------------------------

/** Frees a wasm wrapper if it owns anything. Guarded, so a plain-object double is fine. */
function release(owned: WasmOwned | undefined): void {
  try {
    owned?.free?.();
  } catch {
    // A double-free or an already-freed wrapper must never take a run down with it: the bytes have
    // already been copied out by the time anything here is called.
  }
}

/**
 * Reads one step's bytes out and frees the wrapper, **on every path**.
 *
 * The copy is what makes the free safe: `PracticeStepBytes`'s getters are `getter_with_clone`, so
 * each returns a fresh `Uint8Array` over JavaScript memory rather than a view into the wasm heap.
 */
function drainStep(bytes: PracticeWasmStepBytes): PracticeStepOutput {
  try {
    const coarse = bytes.coarse;
    return {
      detail: bytes.detail,
      index: bytes.index,
      ...(coarse !== undefined && coarse.byteLength > 0 ? { coarse } : {}),
    };
  } finally {
    release(bytes);
  }
}

/** The bounded summary the device reports (**D10**, `rounds-run-summary`). */
function summaryFrom(outcome: PracticeWasmRunOutcome): PracticeRunSummary {
  const colonies = outcome.colonies;
  const rows = colonies.map((row) => {
    try {
      return { colony: row.colony, score: row.living_count };
    } finally {
      release(row);
    }
  });
  return {
    reportedTerminalTick: outcome.terminal_tick,
    colonies: rows,
    // **Empty, and deliberately so.** `rounds-run-summary`'s schema has a sightings collection, but
    // the engine's terminal readout carries none: `SightingsSampler` stays in `waggle-server`
    // (tasks.md 3.5, review **F5**) because it depends on `HumanColonyState`,
    // `waggle_beekeeper::SightingsBuilder` and `FrozenProgramSet`, none of which crossed into
    // `waggle-round` — so there is nothing on this side of the boundary to report. Sending an empty
    // list is the truthful answer; synthesising one from the score rows would hand the Beekeeper
    // fabricated observations, which is worse than handing it none.
    sightings: [],
  };
}

/** Whether `(balanceToml, balancePreset)` is a pair the adapter may act on. */
function balanceIsCoherent(plan: PracticeRunPlan): boolean {
  // The one incoherent pair the adapter can *detect*: a recorded balance with no name for it.
  // `PracticeRunPlan.balancePreset` states the rule — "writing `seed` over a `rounds` geometry
  // produces a manifest whose provenance contradicts its own balance component, which is a lie a
  // trace reader cannot detect" — so this refuses rather than guessing a name.
  //
  // The mirror-image pair (a name with no TOML) is **not** refused, and that asymmetry is deliberate:
  // a server that ran the embedded preset reports exactly that name with no TOML, which is the
  // coherent common case, and the adapter cannot tell it apart from a mis-naming without embedding
  // the server's own preset registry.
  return !(plan.balanceToml !== undefined && plan.balancePreset === undefined);
}

/**
 * **The engine seam over the real bindings.**
 *
 * `configure` translates the module's one-struct-two-arms answer into the seam's tagged union: a
 * present `refusal` is `{kind:'refused'}` — a value, not an exception, exactly as
 * {@link PracticeEngine} requires — and its absence is the run.
 */
export function createPracticeEngine(module: PracticeWasmModule): PracticeEngine {
  return {
    configure(plan: PracticeRunPlan) {
      if (!balanceIsCoherent(plan)) {
        return {
          kind: 'refused' as const,
          reason: 'the run-start response carried a recorded balance with no preset name for it',
        };
      }

      const configured = module.practice_configure(
        String(plan.runId),
        plan.seed,
        // **The one place a rival count still exists on the client** (`home-and-single-player`). It is
        // not a choice and not a default over a player's input: there is no control, no constant model
        // and no request field behind it any more, and a practice run's plan carries no rivals at all.
        // This is the fixed value the ladder is defined by, handed across a wasm ABI that still takes
        // the parameter positionally.
        //
        // ponytail: mirrors a value the engine is taking ownership of. The Rust side is fixing rivals
        // at three inside `practice_configure` and dropping this parameter; when it does, delete this
        // argument and `PracticeRunPlan.rivals` together. Kept until then because the parameter is
        // positional — omitting it now would pass the document where the count belongs — and because
        // the device benchmark's WAG-41 one-colony workload is the one caller that legitimately
        // overrides it.
        //
        // **Read from the module, never restated as a `3` here.** `rounds-local-run` requires the
        // count to be stated once and read from there by both the device that builds the roster and
        // the server that derives the run token's ceilings from it. A TypeScript copy would let
        // someone retune the Rust constant and leave the device building a different world than the
        // server validates — a divergence that surfaces, if at all, only as an out-of-range summary.
        plan.rivals ?? module.practice_rivals(),
        plan.document,
        plan.balancePreset ?? EMBEDDED_BALANCE_PRESET,
        // `undefined` is **omitted**, never sent as an empty string: absent means the embedded preset,
        // and `""` is malformed TOML the engine would refuse.
        plan.balanceToml ?? null,
      );

      const refusal = configured.refusal;
      if (refusal !== undefined) {
        const diagnostics = configured.diagnostics
          .map((d) => {
            try {
              return d.message;
            } finally {
              release(d);
            }
          })
          .join('; ');
        release(configured);
        return {
          kind: 'refused' as const,
          reason: diagnostics.length > 0 ? `${refusal}: ${diagnostics}` : refusal,
        };
      }

      const handle = configured.take_run();
      release(configured);
      if (handle === undefined) {
        // `take_run` yields once. A second call — or a module that answered neither a refusal nor a
        // run — is a refusal here rather than a thrown error: the seam's contract is that a
        // configuration either produces a run or is refused, and inventing a third outcome would make
        // a binding fault indistinguishable from a crash mid-season.
        return {
          kind: 'refused' as const,
          reason: 'the engine yielded no run for an accepted configuration',
        };
      }

      let finished = false;
      const run: PracticeEngineRun = {
        terminalTick: handle.terminal_tick,
        // Read once, here, rather than through the handle later: the handle is freed on the failure
        // and finish paths, and a denominator read off a freed wrapper is exactly the class of bug
        // the eager `drainStep` copy above exists to avoid.
        fieldSize: handle.field_size,

        step(): PracticeStepOutput | null {
          const bytes = handle.step();
          if (bytes === undefined) {
            // Absent means "over", "cancelled", or "failed" — and only `failure` distinguishes them.
            // A write the engine refused must surface as a crash rather than as a completed run,
            // because the driver would otherwise publish a manifest over a truncated trace.
            const failure = handle.failure;
            if (failure !== undefined) {
              const detail = handle.failure_detail;
              release(handle);
              throw new Error(detail === undefined ? failure : `${failure}: ${detail}`);
            }
            return null;
          }
          return drainStep(bytes);
        },

        finish(): PracticeEngineOutcome {
          if (finished) {
            throw new Error('this run has already been finished');
          }
          finished = true;
          const outcome = handle.finish();
          if (outcome === undefined) {
            // **A refusal here is a failure, not an outcome.** `finish` answers nothing when the run
            // had not reached its terminal condition, was cancelled, or failed a write — in every case
            // there is no manifest, so there is nothing to publish and the driver must reclaim.
            const failure = handle.failure ?? 'the run produced no outcome';
            const detail = handle.failure_detail;
            release(handle);
            throw new Error(detail === undefined ? failure : `${failure}: ${detail}`);
          }
          try {
            return {
              manifest: outcome.manifest,
              balance: outcome.balance,
              coarse: outcome.coarse,
              score: outcome.score,
              summary: summaryFrom(outcome),
              roster: outcome.roster,
            };
          } finally {
            release(outcome);
            release(handle);
          }
        },
      };
      return { kind: 'ready' as const, run };
    },

    // Only present when the module actually exports the class — see
    // `PracticeWasmModule.LocalStatsReducer`'s own doc comment for why that is `undefined` only in a
    // pre-existing test double and never in the shipped module.
    ...(module.LocalStatsReducer !== undefined
      ? {
          openReducer(components: {
            manifest: Uint8Array;
            coarse: Uint8Array;
            index: Uint8Array;
            balance: Uint8Array;
            roster: string;
          }): PracticeStatsReducer {
            const handle = module.LocalStatsReducer!.openLocalStatsReducer(
              components.manifest,
              components.coarse,
              components.index,
              components.balance,
              components.roster,
            );
            return {
              nextSpan(): PracticeStatsSpan | undefined {
                const span = handle.nextSpan();
                if (span === undefined) return undefined;
                try {
                  return { offset: span.offset, length: span.length };
                } finally {
                  release(span);
                }
              },
              feed(bytes: Uint8Array): void {
                try {
                  handle.feed(bytes);
                } catch (error) {
                  release(handle);
                  throw error;
                }
              },
              finish(): string {
                try {
                  return handle.finish();
                } finally {
                  release(handle);
                }
              },
            };
          },
        }
      : {}),
  };
}

// ---------------------------------------------------------------------------------------------
// The compile gate
// ---------------------------------------------------------------------------------------------

/**
 * **The on-device compile gate** over `compile_practice_document` (`rounds-local-run` "The document is
 * compiled locally, and a run still starts under server authority").
 *
 * Passes **no balance**, deliberately, and {@link PracticeDocumentCompiler}'s own doc comment carries
 * the traced justification plus the four things that would invalidate it: the resolved balance arrives
 * on the run-start *response*, which this gate precedes by requirement, and the sense radius it would
 * carry can change an emitted operand and the warning list but never the compile **verdict** — so the
 * gate and the run cannot disagree.
 *
 * `typeName` is attributed from the diagnostic's line with the shipped `typeAtLine`, the same
 * inversion `SandboxScreen` already applies to server-side document diagnostics, so the blocked-Run
 * popup's "open the first offending location" control works identically on either path. `''` for a
 * diagnostic above the first `type` header is a real answer the popup already renders.
 */
export function createPracticeDocumentCompiler(
  module: PracticeWasmModule,
): (document: string) => PracticeCompileOutcome {
  return (document: string): PracticeCompileOutcome => {
    const result = module.compile_practice_document(document);
    try {
      if (result.compiles) {
        return { kind: 'compiles' };
      }
      const diagnostics: RunDiagnostic[] = result.diagnostics.map((d) => {
        try {
          return { typeName: typeAtLine(document, d.line) ?? '', message: d.message };
        } finally {
          release(d);
        }
      });
      return { kind: 'failed', diagnostics };
    } finally {
      release(result);
    }
  };
}

/**
 * The compiler a device that **could not load the engine** answers with.
 *
 * Fails closed, and says why. The alternative default — "it compiles" — is precisely the request
 * `rounds-local-run` forbids ("no request before a successful compile"), so a module that will not
 * load must refuse the run rather than let it reach the wire.
 */
export function unavailableEngineCompiler(
  reason: string,
): (document: string) => PracticeCompileOutcome {
  return () => ({
    kind: 'failed',
    diagnostics: [
      { typeName: '', message: `The simulator could not be loaded on this device: ${reason}` },
    ],
  });
}
